import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { GridRowModes } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";

import axios from "axios";

import { API_URL, LIMIT } from "../../config/config.data";
import { dataActions } from "../../store";

// renders an autocomplete component for selecting site parameters
export default function SelectSiteParameters({
  id,
  column,
  field,
  row,
  rowModesModel,
  setRowModesModel,
}) {
  const dispatch = useDispatch();

  const entity = "sites";

  const categoriesEndpoint = "parameters";
  const categoriesEndpointResponseKey = "parameters";
  const categoriesEndpointResponseOptionsKey = "name";

  // check if the field is for ngwos-added parameters
  const ngwosAssociated = field === "ngwos_parameters";

  const previousOptions = row[field];

  // parameters that are associated with the opposite group should be disabled in the autocomplete
  const disabledParameters = ngwosAssociated
    ? row["existing_parameters"]
    : row["ngwos_parameters"];

  // local state
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  // get parameter options
  const getOptions = async (abortController) => {
    try {
      const response = await axios.get(
        `${API_URL}/${categoriesEndpoint}?limit=${LIMIT}`,
        {
          signal: abortController.signal,
        }
      );

      setOptions([
        ...response.data[categoriesEndpointResponseKey].sort((a, b) =>
          a[categoriesEndpointResponseOptionsKey].localeCompare(
            b[categoriesEndpointResponseOptionsKey]
          )
        ),
      ]);
    } catch (error) {
      console.error(error);
    }
  };

  // get sites
  const refreshSites = async () => {
    axios
      .get(`${API_URL}/${entity}?limit=${LIMIT}`)
      .then((response) => {
        dispatch(
          dataActions.setData({
            data: response.data[entity],
            dataName: entity,
          })
        );
        setLoading(false);
      })
      .catch((error) => {
        console.error(`failed to get ${entity}`, error);
        setLoading(false);
      });
  };

  const addOne = async (optionId) => {
    setLoading(true);
    try {
      axios
        .put(
          `${API_URL}/sites/${id}/parameters/${optionId}?ngwos_association=${ngwosAssociated}`
        )
        .then((response) => {
          refreshSites();
        })
        .catch((error) => {
          console.error(`failed to add parameter to ${entity}`, error);
          setLoading(false);
        });
    } catch (error) {
      console.error(`failed to add parameter to ${entity}`, error);
      setLoading(false);
    }
  };

  const deleteOne = async (optionId) => {
    setLoading(true);
    try {
      axios
        .delete(`${API_URL}/sites/${id}/parameters/${optionId}`)
        .then((response) => {
          refreshSites();
        })
        .catch((error) => {
          console.error(`failed to delete parameter from ${entity}`, error);
          setLoading(false);
        });
    } catch (error) {
      console.error(`failed to delete parameter from ${entity}`, error);
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setRowModesModel({
      ...rowModesModel,
      [row.id]: { mode: GridRowModes.View },
    });
  };

  useEffect(() => {
    const abortController = new AbortController();
    getOptions(abortController);
    return () => abortController.abort();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
        <Typography
          sx={{
            width: "200px",
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
        >
          {previousOptions
            .map((option) => option[categoriesEndpointResponseOptionsKey])
            .join(", ")}
        </Typography>
        <IconButton onClick={() => setOpen(true)}>
          <EditIcon />
        </IconButton>
      </Box>
      <Dialog open={open} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
          {`Edit ${column.headerName}`}
          {loading && <CircularProgress />}
        </DialogTitle>
        <DialogContent>
          <Autocomplete
            multiple
            id={field}
            options={options}
            limitTags={5}
            getOptionLabel={(option) =>
              option[categoriesEndpointResponseOptionsKey]
            }
            defaultValue={previousOptions}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(event, newValue) => {
              // newValue is an array of selected options
              newValue.forEach((option) => {
                if (!previousOptions.includes(option)) {
                  addOne(option.id);
                }
              });

              previousOptions.forEach((option) => {
                if (!newValue.includes(option)) {
                  deleteOne(option.id);
                }
              });
            }}
            renderInput={(params) => (
              <Box sx={{ my: 1 }}>
                <TextField {...params} label={column.headerName} />
              </Box>
            )}
            renderOption={(props, option) => {
              // Define the condition for disabling the option
              const isDisabled = disabledParameters.some(
                (parameter) => parameter.id === option.id
              );
              return (
                <li
                  {...props}
                  style={{
                    pointerEvents: isDisabled ? "none" : "auto",
                    opacity: isDisabled ? 0.5 : 1,
                  }}
                >
                  {option[categoriesEndpointResponseOptionsKey]}
                </li>
              );
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleCloseDialog}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
